import { initSentry } from 'src/sentry.ts';
import { createClient } from 'src/createClient.ts';
import {
    CountriesDocument, LocalesEnum,
    MessagesDocument,
    MessagesQuery,
    MessagesQueryVariables,
} from 'src/generated/operations.ts';
import { parseSettingsFromUrlOnBelimoHost } from 'src/bootstrap/parseSettingsFromUrlOnBelimoHost.ts';
import { defaultUserSettings } from 'src/bootstrap/constants.ts';
import { ApolloClient } from '@apollo/client/core';
import { UserSettings } from 'src/types/UserSettings.ts';
import { createOfflineDatabase } from 'src/database/createOfflineDatabase.ts';
import { StorageKeys } from 'shared/interfaces/InitProjectsPageOptions.ts';
import { UserLocalOptionsClientInterface } from 'src/common/UserLocalOptionsClient/UserLocalOptionsClientInterface';
import { createUserLocalOptionsClient } from 'src/common/UserLocalOptionsClient/createUserLocalOptionsClient';

export type Messages = {
    messages: Omit<MessagesQuery['vssI18n'], '__typename'>;
    attributes: Record<string, string>;
    language: string;
}

export async function loadMessages(client: ApolloClient<unknown>, locale: LocalesEnum): Promise<Messages> {
    const { data } = await client.query<MessagesQuery, MessagesQueryVariables>({
        query: MessagesDocument,
        variables: { locale },
    });
    return {
        messages: data.vssI18n,
        attributes: JSON.parse(data.attributesI18n),
        language: data.vssLanguage,
    };
}

export async function bootstrap(
    sentryHost: string,
    uri: string,
    global: Window = window,
    storageKeys: Partial<StorageKeys> = {},
): Promise<{
        userSettings: UserSettings;
        messages: Messages;
        databaseCache: LocalForage;
        offlineDatabase: LocalForage;
        projectsStore: LocalForage;
        userOptionsClient: UserLocalOptionsClientInterface;
    }> {
    initSentry(sentryHost, window.location.hostname);
    const clientInstance = createClient(`${uri}/graphql`);
    const { data } = await clientInstance.query({ query: CountriesDocument });
    const userSettings = parseSettingsFromUrlOnBelimoHost(data, global) ?? defaultUserSettings;
    const messages = await loadMessages(clientInstance, userSettings.currentLocale);
    const {
        databaseCache, offlineDatabase, projectsStore, userOptionsStore,
    } = createOfflineDatabase(storageKeys);
    await projectsStore.ready();
    const userOptionsClient = await createUserLocalOptionsClient(userOptionsStore);
    return {
        userSettings,
        messages,
        databaseCache,
        offlineDatabase,
        projectsStore,
        userOptionsClient,
    };
}

export type BootstrapFn = typeof bootstrap;
